import styled from '@emotion/styled';
import { Suspense } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';

import type { FreeTextbooksSyllabusSelectorFooterQuery } from '__free_textbooks_generated__/FreeTextbooksSyllabusSelectorFooterQuery.graphql';
import ErrorBoundaryWithRetry from 'ms-components/ErrorBoundaryWithRetry';
import { FreeTextbooksBookDemoButton } from 'ms-pages/FreeTextbooks/FreeTextbooksBookDemoButton';
import type { CountryCode } from 'ms-pages/FreeTextbooks/types';
import {
  font,
  fontFamily,
  fontWeight,
  fontSize,
  lineHeight,
} from 'ms-styles/base';
import { colors } from 'ms-styles/colors';
import LinkButton from 'ms-ui-primitives/Button/ReactRouterLinkButton';
import { multilineTextOverflow, tappable } from 'ms-utils/emotion';

import * as FLAGS from './assets/flags';
import {
  FlagsWrapper,
  ButtonFlagWrapper,
  FlagWrapper,
  FlagLabel,
  Flag,
} from '../elements';
import { getCurriculumSelectorUrl } from '../urls';

const Wrapper = styled.div({
  fontFamily: fontFamily.body,
  fontWeight: fontWeight.normal,
  color: colors.white,
  fontSize: 14,
  lineHeight: '18px',
});

const Title = styled.h2({
  color: colors.almond50,
  ...multilineTextOverflow(2),
  ...font.gilroyExtraLargeHeading,
  ...tappable,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingTop: 16,
  paddingBottom: 40,
});

const ContactUsWrapper = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexWrap: 'wrap',
  fontFamily: fontFamily.body,
  color: colors.almond50,
  fontSize: fontSize.medium,
  lineHeight: lineHeight.body,
});

const BookDemoWrapper = styled.div({
  paddingLeft: 48,
});

const LineSeparator = styled.div({
  height: 2,
  backgroundColor: colors.grey90,
  marginTop: 40,
});

const styles = {
  stretch: {
    alignItems: 'stretch',
  },
  whiteColor: {
    color: colors.white,
  },
};

const FREE_TEXTBOOKS_SYLLABUS_SELECTOR_FOOTER_QUERY = graphql`
  query FreeTextbooksSyllabusSelectorFooterQuery {
    countries {
      id
      code
      name
    }
  }
`;

type Props = { countryCode: CountryCode };

export function FreeTextbooksSyllabusSelectorFooter({ countryCode }: Props) {
  return (
    <ErrorBoundaryWithRetry name="FreeTextbooksSyllabusSelectorFooter">
      {({ fetchKey }) => (
        <Suspense fallback={<div>Loading...</div>}>
          <FreeTextbooksSyllabusSelectorFooterInner
            fetchKey={fetchKey}
            countryCode={countryCode}
          />
        </Suspense>
      )}
    </ErrorBoundaryWithRetry>
  );
}

function FreeTextbooksSyllabusSelectorFooterInner({
  fetchKey,
  countryCode,
}: Props & { fetchKey: string }) {
  const data = useLazyLoadQuery<FreeTextbooksSyllabusSelectorFooterQuery>(
    FREE_TEXTBOOKS_SYLLABUS_SELECTOR_FOOTER_QUERY,
    {}, // variables
    { fetchKey },
  );
  const { countries } = data;

  type Flag = Omit<(typeof countries)[number], 'code'> & { code: CountryCode };

  const flags: Flag[] = [
    ...countries,
    // Add the international flag
    {
      id: 'international',
      code: 'international',
      name: 'International',
    } as const,
  ]
    // ignore the currently selected countryCode
    .filter(c => c.code !== countryCode);

  return (
    <Wrapper>
      <Title>We also cover</Title>
      <FlagsWrapper>
        {flags.map(flag => (
          <ButtonFlagWrapper key={flag.code}>
            <LinkButton
              styles={{ ...styles.stretch, ...styles.whiteColor }}
              color="cloudBurst"
              label={flag.name}
              to={getCurriculumSelectorUrl({ countryCode: flag.code })}
            >
              <FlagWrapper>
                <Flag alt={flag.code} src={getFlagUrl(flag.code)} />
                <FlagLabel>{flag.name}</FlagLabel>
              </FlagWrapper>
            </LinkButton>
          </ButtonFlagWrapper>
        ))}
      </FlagsWrapper>
      <ContactUsWrapper>
        Learn more about Mathspace
        <BookDemoWrapper>
          <FreeTextbooksBookDemoButton
            buttonLabel="Chat to us"
            trackingId="FreeTextbook/SyllabusSelectorFooter"
            snowplowEventLabel="curriculum_selector"
            color="almond50"
          />
        </BookDemoWrapper>
      </ContactUsWrapper>
      <LineSeparator />
    </Wrapper>
  );
}

function getFlagUrl(countryCode: CountryCode) {
  switch (countryCode) {
    case 'AU':
    case 'CA':
    case 'GB':
    case 'HK':
    case 'IN':
    case 'international':
    case 'NZ':
    case 'SG':
    case 'US':
      return FLAGS[countryCode];
    default:
      return FLAGS.GenericFlag;
  }
}
